import { AxiosError, AxiosResponse } from 'axios'
import { parseISO } from 'date-fns'
import { RouteParameters, ToastSubType } from '../../../../constants'
import {
  Appointment,
  AppointmentCancelRequest,
  AppointmentCreateRequest,
  AppointmentCustomer,
  AppointmentGetRequest,
  AppointmentRescheduleRequest,
  AppointmentResponseAppointment,
  AppointmentsGetManyRequest,
  AppointmentsPageResponse,
  AppointmentsPageResponseRaw,
  Customer,
} from '../../../../types'
import { hydratePathname } from '../../../../util'
import { HttpClientConfig } from '../../../useHttpClientConfig'
import { ToastNotifyFunction } from '../../../useNotification'
import { apiVersion, service } from '../service'
import { applySearchParams, urlToPathnameAndSearch } from '../utils'

type AppointmentCancelRequestSansAppointmentId = Omit<AppointmentCancelRequest, 'appointmentId'>

type AppointmentRescheduleRequestTransformed = Omit<
  AppointmentRescheduleRequest,
  'appointmentIdRescheduled' | 'appointmentIdNew' | 'newAppointmentRequest'
> & {
  replacement_appointment: AppointmentCreateRequest
}

interface ResponseData<T> {
  data: T
}

const Routes = Object.freeze({
  APPOINTMENT_CANCEL: `/api/${apiVersion}/appointments/:${RouteParameters.APPOINTMENT_ID}/cancel`,
  APPOINTMENT_RESCHEDULE: `/api/${apiVersion}/appointments/:${RouteParameters.APPOINTMENT_ID}/reschedule`,
  APPOINTMENTS: `/api/${apiVersion}/appointments`,
})

const toCanonicalCustomer = (customer: AppointmentCustomer): Customer => ({
  createdAt: customer.created_at,
  dateOfBirth: customer.date_of_birth,
  email: customer.email,
  familyName: customer.family_name,
  gid: customer.gid,
  givenName: customer.given_name,
  id: customer.id,
  phoneNumber: customer.phone_number,
  salesforceId: customer.salesforce_id,
  salutation: customer.salutation,
  timezone: customer.timezone,
  updatedAt: customer.updated_at,
})

const transformAppointmentResponseAppointment = ({
  created_at,
  customer,
  start_date,
  status,
  updated_at,
  ...appointment
}: AppointmentResponseAppointment): Appointment => ({
  ...appointment,
  created_at: parseISO(created_at),
  customer: toCanonicalCustomer(customer),
  start_date: parseISO(start_date),
  status: {
    ...status,
    created_at: parseISO(status.created_at as unknown as string),
  },
  updated_at: parseISO(updated_at),
})

const makeCreateAppointment =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async (request: AppointmentCreateRequest): Promise<Appointment> => {
    const pathname = Routes.APPOINTMENTS

    const { data } = await service(baseUrl)
      .post<AppointmentCreateRequest, AxiosResponse<ResponseData<AppointmentResponseAppointment>>>(pathname, request, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to create an appointment took too long. It might still have been created. Please check in your calendar system to check if it worked before trying again.`,
            type: ToastSubType.CREATION_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return transformAppointmentResponseAppointment(data.data)
  }

const makeCancelAppointment =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async ({ appointmentId, ...rest }: AppointmentCancelRequest): Promise<Appointment> => {
    const pathname = hydratePathname(Routes.APPOINTMENT_CANCEL, {
      [RouteParameters.APPOINTMENT_ID]: appointmentId,
    })

    const { data } = await service(baseUrl)
      .patch<AppointmentCancelRequestSansAppointmentId, AxiosResponse<ResponseData<AppointmentResponseAppointment>>>(pathname, rest, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to cancel an appointment took too long. It might still have been canceled. Please check in your calendar system to check if it worked before trying again.`,
            type: ToastSubType.CANCELLATION_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return transformAppointmentResponseAppointment(data.data)
  }

const makeRescheduleAppointment =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async ({ appointmentIdRescheduled, newAppointmentRequest, description, reason }: AppointmentRescheduleRequest): Promise<Appointment> => {
    const pathname = hydratePathname(Routes.APPOINTMENT_RESCHEDULE, {
      [RouteParameters.APPOINTMENT_ID]: appointmentIdRescheduled,
    })

    const request: AppointmentRescheduleRequestTransformed = {
      description,
      reason,
      replacement_appointment: newAppointmentRequest,
    }

    const { data } = await service(baseUrl)
      .patch<AppointmentRescheduleRequestTransformed, AxiosResponse<ResponseData<AppointmentResponseAppointment>>>(
        pathname,
        request,
        config
      )
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to reschedule an appointment took too long. It might still have been rescheduled. Please check in your calendar system to check if it worked before trying again.`,
            type: ToastSubType.RESCHEDULE_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return transformAppointmentResponseAppointment(data.data)
  }

const makeGetAppointment =
  (baseUrl: string, config: HttpClientConfig) =>
  async ({ id, gid }: AppointmentGetRequest): Promise<Appointment | undefined> => {
    const placeholderOrigin = 'http://placeholder.com'
    const url = new URL(Routes.APPOINTMENTS, placeholderOrigin)

    if (id) {
      url.searchParams.set('id', id)
    }
    if (gid) {
      url.searchParams.set('gid', gid)
    }

    const pathname = url.href.replace(placeholderOrigin, '')

    const { data } = await service(baseUrl).get<AppointmentsPageResponseRaw>(pathname, config)
    const [appointmentResponse] = data.data

    return appointmentResponse ? transformAppointmentResponseAppointment(appointmentResponse) : undefined
  }

const makeGetAppointments =
  (baseUrl: string, config: HttpClientConfig) =>
  async ({ page = 0, page_size = 100, ...request }: AppointmentsGetManyRequest): Promise<AppointmentsPageResponse> => {
    const placeholderOrigin = 'http://placeholder.com'
    const url = new URL(Routes.APPOINTMENTS, placeholderOrigin)

    applySearchParams(url, { ...request, page, page_size })

    const pathnameAndSearch = urlToPathnameAndSearch(url)
    const { data: appointmentResponse } = await service(baseUrl).get<AppointmentsPageResponseRaw>(pathnameAndSearch, config)

    return {
      ...appointmentResponse,
      data: appointmentResponse.data.map(transformAppointmentResponseAppointment),
    }
  }

export {
  makeCancelAppointment,
  makeCreateAppointment,
  makeGetAppointment,
  makeGetAppointments,
  makeRescheduleAppointment,
  transformAppointmentResponseAppointment,
}
